import { inject } from '@angular/core';
import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { Router } from '@angular/router';
import { Capacitor } from '@capacitor/core';
import { throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { AuthStore } from '../../features/auth/store/auth.store';
import { StorageService } from '../services/storage.service';
import { LocationStore } from '../store/location.store';
import { SKIP_AUTH_REDIRECT_HEADER, SKIP_LOCATION_ID_HEADER } from '../api/api.models';
import { environment } from '../../../environments/environment';
import { WINDOW } from '../tokens/window.token';

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const storage = inject(StorageService);
  const authStore = inject(AuthStore);
  const router = inject(Router);
  const locationStore = inject(LocationStore);
  const win = inject(WINDOW);
  const isOwnApiRequest = req.url.startsWith(environment.endPoint);

  // Third-party APIs (e.g. ipinfo.io) must never receive our session headers — ipinfo reads
  // `Authorization: Bearer …` as its own API token and rejects the request with 403.
  const token = isOwnApiRequest ? storage.getToken() : null;

  // Native (Capacitor) app: the WebView's real origin is always `localhost`/`capacitor://localhost`,
  // which the backend can't resolve to any company — use the configured tenant domain instead.
  // Web: use the real runtime host so the backend resolves the tenant from the actual browser domain.
  // Read via the injected WINDOW token (not the raw global) — see window.token.ts's doc comment;
  // `window.location` is Unforgeable so it can't be stubbed directly in a spec.
  const requestUrl = Capacitor.isNativePlatform() ? environment.domain : win.location.host;
  const headers: Record<string, string> = isOwnApiRequest ? { RequestUrl: requestUrl } : {};
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  // Opted out by the caller (ApiRequestOptions.skipLocationId) — e.g. DoctorStation/GetWorklist,
  // which is scoped by doctor id rather than the user's currently selected location.
  const skipLocationId = req.headers.has(SKIP_LOCATION_ID_HEADER);
  const needsLocationId = isOwnApiRequest && !skipLocationId && !req.params.has('locationId');

  // Opted out by the caller (ApiRequestOptions.skipAuthRedirect) — strip the marker so it
  // never reaches the backend, and leave the 401/403 handling below to the caller.
  const skipAuthRedirect = req.headers.has(SKIP_AUTH_REDIRECT_HEADER);

  let outgoingHeaders = req.headers;
  if (skipAuthRedirect) outgoingHeaders = outgoingHeaders.delete(SKIP_AUTH_REDIRECT_HEADER);
  if (skipLocationId) outgoingHeaders = outgoingHeaders.delete(SKIP_LOCATION_ID_HEADER);

  const authReq = req.clone({
    setHeaders: headers,
    headers: outgoingHeaders,
    params: needsLocationId ? req.params.set('locationId', locationStore.locationId()) : req.params,
  });

  return next(authReq).pipe(
    catchError((error: HttpErrorResponse) => {
      // Only our own API's 401/403 mean "session invalid" / "access denied" — a third-party
      // failure must never log the user out or bounce them to the dashboard.
      if (skipAuthRedirect || !isOwnApiRequest) return throwError(() => error);

      if (error.status === 401) {
        // Token expired or invalid — clear session and redirect
        storage.clearAuthData();
        authStore.clearAuth();
        router.navigate(['/login']);
        return throwError(() => error);
      }

      if (error.status === 403) {
        router.navigate(['/dashboard']);
        return throwError(() => error);
      }

      return throwError(() => error);
    }),
  );
};
